import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  KeyOutlined,
  ShoppingCartOutlined,
  CrownOutlined,
  GlobalOutlined,
  CloudServerOutlined,
  ShareAltOutlined,
  DollarOutlined,
  BarChartOutlined,
  TeamOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/ant-design-pro/api';
import { normalizeLocale } from '@/utils/locale';
import HeaderDropdown from '../HeaderDropdown';
import UVStatusModal from '../UVStatusModal';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const intl = useIntl();
  const locale = normalizeLocale(intl.locale);
  const label =
    {
      'zh-CN': '个人中心',
      'en-US': 'Profile',
      'ja-JP': 'プロフィール',
    }[locale] || '个人中心';
  return <span className="anticon">{label}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const intl = useIntl();
  const locale = normalizeLocale(intl.locale);
  const tx = (zh: string, en: string, ja?: string) => {
    if (locale === 'en-US') {
      return en;
    }
    if (locale === 'ja-JP') {
      return ja || en;
    }
    return zh;
  };
  const [uvModalVisible, setUvModalVisible] = useState(false);

  /**
   * 退出登录，不将当前业务页面带入下一次登录。
   */
  const loginOut = async () => {
    try {
      await outLogin();
    } catch (error) {
      console.error('退出登录API调用失败:', error);
    }
    
    // 清除本地存储的token
    localStorage.removeItem('token');
    
    history.replace('/user/login');
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'change-password') {
      history.push('/user/change-password');
      return;
    }
    if (key === 'purchase-history') {
      history.push('/user/purchase-history');
      return;
    }
    if (key === 'my-domain') {
      history.push('/user-domain');
      return;
    }
    if (key === 'domain-rental') {
      history.push('/user/domain-rental');
      return;
    }
    if (key === 'membership-buy') {
      history.push('/membership/buy');
      return;
    }
    if (key === 'my-shares') {
      history.push('/user/my-shares');
      return;
    }
    if (key === 'my-agent') {
      history.push('/user/my-agent');
      return;
    }
    if (key === 'pricing') {
      history.push('/pricing');
      return;
    }
    if (key === 'uv-status') {
      setUvModalVisible(true);
      return;
    }
    if (key === 'docs') {
      history.push('/docs');
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser, siteConfig } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  // 判断是否隐藏购买功能
  const hidePurchaseFeature = siteConfig?.hidePurchaseFeature || false;
  const hidePricingFeature = siteConfig?.hidePricingFeature || false;
  const hideDomainRentalFeature = (siteConfig as any)?.hideDomainRentalFeature || false;

  const menuItems = [
    ...(menu
      ? [
          ...(!hidePurchaseFeature ? [
          {
            key: 'membership-buy',
            icon: <CrownOutlined />,
            label: tx('会员购买', 'Membership', '会員購入'),
          },
          {
            key: 'purchase-history',
            icon: <ShoppingCartOutlined />,
            label: tx('购买记录', 'Purchase History', '購入履歴'),
          },
          ] : []),
          ...(!hidePricingFeature ? [
          {
            key: 'pricing',
            icon: <DollarOutlined />,
            label: tx('套餐购买', 'Plan Purchase', 'プラン購入'),
          },
          ] : []),
          {
            key: 'my-domain',
            icon: <CloudServerOutlined />,
            label: tx('我的域名', 'My Domains', 'マイドメイン'),
          },
          ...(!hideDomainRentalFeature ? [
          {
            key: 'domain-rental',
            icon: <GlobalOutlined />,
            label: tx('域名租用', 'Domain Rental', 'ドメインレンタル'),
          },
          ] : []),
          {
            key: 'my-shares',
            icon: <ShareAltOutlined />,
            label: tx('分享管理', 'Share Management', '共有管理'),
          },
          {
            key: 'my-agent',
            icon: <TeamOutlined />,
            label: tx('我的推广', 'My Referrals', '紹介管理'),
          },
          {
            key: 'uv-status',
            icon: <BarChartOutlined />,
            label: tx('使用查询', 'Usage Lookup', '利用照会'),
          },
          {
            key: 'docs',
            icon: <BookOutlined />,
            label: tx('使用文档', 'Documentation', 'ドキュメント'),
          },
          {
            key: 'change-password',
            icon: <KeyOutlined />,
            label: tx('修改密码', 'Change Password', 'パスワード変更'),
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: tx('退出登录', 'Log Out', 'ログアウト'),
    },
  ];

  return (
    <>
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
      <UVStatusModal
        visible={uvModalVisible}
        onCancel={() => setUvModalVisible(false)}
      />
    </>
  );
};
